import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { FaPuzzlePiece } from 'react-icons/fa6';
import { ThemeProvider } from 'styled-components';

import { useQuiz } from '@quizzach/api-hooks';
import { Bulb, EndFlag, Pause } from '@quizzach/assets';
import { Navigation } from '@quizzach/components';
import { QuestionType, RoutePath } from '@quizzach/interfaces';
import {
	BackgroundContainer,
	Colors,
	kidsPlayTypography,
} from '@quizzach/styles';
import {
	FramedCover,
	NavPlayProgress,
	PremadeQuizPlayBackground,
	ProgressBar,
	QuestionNumber,
	QuizContainer,
	QuizNavigator,
} from '@quizzach/play-styles';

import { useThemeContext, useWindowListener } from '@quizzach/contexts';

export const Quiz = () => {
	const {
		quiz,
		mode,
		quizType,
		halfLength,
		halftime,
		currentQuestionNumber,
		error,
		handleSetHalftime,
		handleSetMode,
		handleNext,
		handlePrevious,
		setFeedback,
		setNavigationType,
		featuredQuiz,
		author,
		currentStep,
		progressPercentage,
	} = useQuiz();

	const { touchDevice } = useWindowListener();
	const { setThemeOverride } = useThemeContext();
	const location = useLocation();
	const [coverLoaded, setCoverLoaded] = useState(false);

	// Quiz play is dark-only: force dark for the whole play tree and hand the
	// user their saved theme back the moment they leave.
	useEffect(() => {
		setThemeOverride('dark');
		return () => setThemeOverride(null);
	}, [setThemeOverride]);

	// for_kids: same stage, DynaPuff voice — typography tokens swap for the
	// whole play tree, everything else (layout, colors, flow) stays
	const kidsQuiz = quiz?.premade_quiz?.for_kids === true;

	const premadePlayBackgroundImage = quiz?.premade_quiz?.play_cover_photo
		? `${import.meta.env.VITE_ASSETS_URL}/thematic_quizzes/${
				quiz.premade_quiz.play_cover_photo
			}`
		: undefined;
	const standardPlayBackgroundImage =
		quizType === 'quick' || quizType === 'generator'
			? '/quiz-play-backgrounds/default_play_cover.webp'
			: undefined;
	const isQuizStart = location.pathname.endsWith(`/${RoutePath.QuizStart}`);
	const isModeSelection = location.pathname.endsWith(
		`/${RoutePath.QuizModeSelection}`
	);
	const isTvCode = location.pathname.endsWith(`/${RoutePath.QuizPlay}`);
	// Every screen of the play tree shares the framed backdrop — the TV code
	// and mode selection entry screens included, on phones as well as desktop:
	// the quiz looks at you while you choose where and how to play it.
	const heroBackgroundImage =
		premadePlayBackgroundImage ?? standardPlayBackgroundImage;
	// entry screens share the taller frame (no progress bar above them), but
	// only the TV code and mode screens stack rows over the cover — those
	// blur the photo on phones; /start and the slides keep it sharp
	const isEntryScreen = isTvCode || isModeSelection || isQuizStart;
	const blurCover = isTvCode || isModeSelection;
	const showQuizNavigator = !isModeSelection && !isQuizStart && !isTvCode;
	const currentQuestionType = currentQuestionNumber
		? quiz?.questions?.[currentQuestionNumber - 1]?.type
		: undefined;
	const isEstimationStep =
		currentQuestionType === QuestionType.estimation &&
		(currentStep?.type === 'question' || currentStep?.type === 'answer');
	const isChapterStep =
		currentStep?.type === 'question-chapter' ||
		currentStep?.type === 'question-chapter-end' ||
		currentStep?.type === 'answer-chapter' ||
		currentStep?.type === 'answer-chapter-end';
	const isPuzzleStep =
		location.pathname.endsWith(`/${RoutePath.QuizPuzzleIntro}`) ||
		location.pathname.endsWith(`/${RoutePath.QuizPuzzleAnswer}`);
	const isQuizEnd = location.pathname.endsWith(`/${RoutePath.QuizEnd}`);

	// Desktop presenters drive the quiz from the keyboard: ← → page the
	// slides everywhere the navigator shows (not on start/mode/end screens,
	// and never while typing into a field or an open modal).
	useEffect(() => {
		if (touchDevice || !showQuizNavigator || isQuizEnd) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
			const target = event.target as HTMLElement | null;
			if (
				target &&
				(target.isContentEditable ||
					['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
			) {
				return;
			}
			// mounted-but-hidden modals keep empty portal containers in #modal,
			// so only a container WITH content means a dialog is actually open
			const modalRoot = document.getElementById('modal');
			if (
				modalRoot &&
				Array.from(modalRoot.children).some(
					(child) => child.childElementCount > 0
				)
			) {
				return;
			}
			if (event.key === 'ArrowRight') handleNext();
			else handlePrevious();
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [touchDevice, showQuizNavigator, isQuizEnd, handleNext, handlePrevious]);

	// Estimation steps (intro break + question/answer) show a »procjena«
	// wordmark in the indicator slot instead of an icon.
	const showEstimationLabel =
		isEstimationStep ||
		(currentStep?.type === 'break' &&
			location.pathname.endsWith(`/${RoutePath.QuizEstimationIntro}`));

	const BreakNavigatorIcon = isChapterStep
		? EndFlag
		: isPuzzleStep
			? FaPuzzlePiece
			: currentStep?.type !== 'break'
				? null
				: location.pathname.endsWith(`/${RoutePath.QuizHalftime}`)
					? Pause
					: location.pathname.endsWith(`/${RoutePath.QuizEnd}`)
						? EndFlag
						: Bulb;

	// One indicator, two homes: inside QuizNavigator on mobile, inside the
	// top play bar (NavPlayProgress) on desktop — CSS picks which one shows.
	const navigatorIndicator = (
		<div className='quiz-navigator-indicator'>
			{showEstimationLabel ? (
				<div className='quiz-navigator-text'>procjena</div>
			) : BreakNavigatorIcon ? (
				<div className='quiz-navigator-icon'>
					<BreakNavigatorIcon
						size={touchDevice ? 24 : 28}
						color={isPuzzleStep ? '#00ffc2' : Colors.brandMain}
					/>
				</div>
			) : currentQuestionNumber ? (
				<div className='quiz-navigator-numbers'>
					<QuestionNumber>{currentQuestionNumber}</QuestionNumber>
					<QuestionNumber $small>
						/ {(quiz?.questions?.length ?? 1) - 1}
					</QuestionNumber>
				</div>
			) : null}
		</div>
	);

	return (
		<ThemeProvider
			theme={(outerTheme) =>
				kidsQuiz
					? { ...outerTheme, typography: kidsPlayTypography }
					: outerTheme
			}
		>
			{/* The shared container's 70px footer clearance would keep the play
			    stage scrollable even when a slide fits the viewport. */}
			<BackgroundContainer
				color={Colors.quizBackground}
				style={{ paddingBottom: 0 }}
			>
				<PremadeQuizPlayBackground $kidsFonts={kidsQuiz}>
					<div className='quiz-play-background-content'>
						{/* The cover is an inset rounded backdrop that dissolves
					    into the page — espresso-washed in dark, bleached to
					    cream (»bijeljeni zastor«) in light. */}
						{heroBackgroundImage && (
							<FramedCover
								aria-hidden='true'
								$entry={isEntryScreen}
								$blur={blurCover}
							>
								<img
									src={heroBackgroundImage}
									alt=''
									className={coverLoaded ? 'loaded' : undefined}
									onLoad={() => setCoverLoaded(true)}
								/>
							</FramedCover>
						)}
						<Navigation
							hideSecondaryItems
							featuredAuthor={featuredQuiz}
							authorDisplayName={author}
							closeQuizToProfileOnTouchDevice
							bareTopNav
							topNavCenter={
								showQuizNavigator ? (
									<NavPlayProgress>
										<ProgressBar $progress={progressPercentage} />
										{navigatorIndicator}
									</NavPlayProgress>
								) : undefined
							}
						/>

						<QuizContainer className='flex-column noselect'>
							{showQuizNavigator && (
								<QuizNavigator>
									{navigatorIndicator}
									<ProgressBar $progress={progressPercentage} />
								</QuizNavigator>
							)}
							<Outlet
								context={{
									quiz,
									mode,
									quizType,
									halftime,
									currentQuestionNumber,
									error,
									halfLength,
									handleNext,
									handlePrevious,
									handleSetHalftime,
									handleSetMode,
									setFeedback,
									setNavigationType,
								}}
							/>
						</QuizContainer>
					</div>
				</PremadeQuizPlayBackground>
			</BackgroundContainer>
		</ThemeProvider>
	);
};
